// API Service for Sayaara Car Marketplace
// Handles all communication with the backend API

const API_BASE_URL = 'https://sayaara-backend.oussama-ad8.workers.dev';

export interface CarListing {
  id: number;
  dealer_id: number;
  make: string;
  model: string;
  year: number;
  price: number;
  monthly_payment?: number;
  term_months?: number;
  min_credit_score?: number;
  down_payment?: number;
  vehicle_type?: string;
  mileage?: number;
  fuel_type?: string;
  transmission?: string;
  color?: string;
  description?: string;
  images?: string[];
  features?: string[];
  vin?: string;
  status?: 'active' | 'sold' | 'pending';
  location?: string;
  contact_phone?: string;
  contact_email?: string;
  dealer_name?: string;
  dealer_logo?: string;
  dealer_city?: string;
  dealer_state?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Dealer {
  id: number;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  logo_url?: string;
  description?: string;
  website?: string;
  verified: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateCarListingRequest {
  dealer_id: number;
  make: string;
  model: string;
  year: number;
  price: number;
  monthly_payment?: number;
  term_months?: number;
  min_credit_score?: number;
  down_payment?: number;
  vehicle_type?: string;
  mileage?: number;
  fuel_type?: string;
  transmission?: string;
  color?: string;
  vin?: string;
  description?: string;
  images?: string[];
  features?: string[];
  location?: string;
  contact_phone?: string;
  contact_email?: string;
}

export interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  status: 'new' | 'read' | 'replied';
  created_at?: string;
  updated_at?: string;
}

export interface BuyerApplication {
  id: number;
  car_listing_id?: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  income?: number;
  employment_status?: string;
  credit_score_range?: string;
  down_payment?: number;
  loan_amount?: number;
  loan_term?: number;
  status: 'pending' | 'approved' | 'rejected' | 'processed';
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Advertisement {
  id: number;
  title: string;
  description?: string;
  image_url?: string;
  link_url?: string;
  start_date?: string;
  end_date?: string;
  active: boolean;
  position?: string;
  created_at?: string;
  updated_at?: string;
}

// Generic API response wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Paginated response wrapper
interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    offset: number;
  };
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Don't set Content-Type for FormData requests (let browser handle it)
    const headers: Record<string, string> = {};
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    const response = await fetch(url, {
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  // Car Listings API
  async getCarListings(params?: {
    page?: number;
    limit?: number;
    make?: string;
    model?: string;
    status?: string;
    dealer_id?: number;
    min_price?: number;
    max_price?: number;
    min_year?: number;
    max_year?: number;
  }): Promise<PaginatedResponse<CarListing>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<any>(
      `/api/listings?${searchParams.toString()}`
    );

    // Handle nested response structure from backend
    if (response.data && Array.isArray(response.data)) {
      return response as PaginatedResponse<CarListing>;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return {
        success: response.success,
        data: response.data.data,
        pagination: response.data.pagination
      } as PaginatedResponse<CarListing>;
    }

    return response as PaginatedResponse<CarListing>;
  }

  async getCarListing(id: number): Promise<CarListing> {
    const response = await this.request<ApiResponse<CarListing>>(`/api/listings/${id}`);
    return response.data;
  }

  async createCarListing(data: CreateCarListingRequest): Promise<CarListing> {
    const response = await this.request<ApiResponse<CarListing>>('/api/listings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateCarListing(id: number, data: Partial<CreateCarListingRequest>): Promise<CarListing> {
    const response = await this.request<ApiResponse<CarListing>>(`/api/listings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async deleteCarListing(id: number): Promise<void> {
    await this.request(`/api/listings/${id}`, {
      method: 'DELETE',
    });
  }

  // Dealers API
  async getDealers(params?: {
    page?: number;
    limit?: number;
    verified?: boolean;
  }): Promise<PaginatedResponse<Dealer>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<PaginatedResponse<Dealer>>(
      `/api/dealers?${searchParams.toString()}`
    );
    return response;
  }

  async getDealer(id: number): Promise<Dealer> {
    const response = await this.request<ApiResponse<Dealer>>(`/api/dealers/${id}`);
    return response.data;
  }

  async createDealer(data: Omit<Dealer, 'id' | 'created_at' | 'updated_at'>): Promise<Dealer> {
    const response = await this.request<ApiResponse<Dealer>>('/api/dealers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateDealer(id: number, data: Partial<Dealer>): Promise<Dealer> {
    const response = await this.request<ApiResponse<Dealer>>(`/api/dealers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async deleteDealer(id: number): Promise<void> {
    await this.request(`/api/dealers/${id}`, {
      method: 'DELETE',
    });
  }

  async dealerLogin(credentials: { email?: string; login_username?: string; password: string }): Promise<ApiResponse<{ dealer: Dealer; sessionToken: string }>> {
    const response = await this.request<ApiResponse<{ dealer: Dealer; sessionToken: string }>>('/api/dealers/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    return response;
  }

  // Contact API
  async submitContact(data: Omit<ContactSubmission, 'id' | 'status' | 'created_at' | 'updated_at'>): Promise<ContactSubmission> {
    const response = await this.request<ApiResponse<ContactSubmission>>('/api/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async getContactSubmissions(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<ContactSubmission>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<PaginatedResponse<ContactSubmission>>(
      `/api/contact?${searchParams.toString()}`
    );
    return response;
  }

  // Applications API
  async submitApplication(data: Omit<BuyerApplication, 'id' | 'status' | 'created_at' | 'updated_at'>): Promise<BuyerApplication> {
    const response = await this.request<ApiResponse<BuyerApplication>>('/api/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async getApplications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    car_listing_id?: number;
  }): Promise<PaginatedResponse<BuyerApplication>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<PaginatedResponse<BuyerApplication>>(
      `/api/applications?${searchParams.toString()}`
    );
    return response;
  }

  // Advertisements API
  async getAdvertisements(params?: {
    page?: number;
    limit?: number;
    active?: boolean;
    position?: string;
  }): Promise<PaginatedResponse<Advertisement>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await this.request<PaginatedResponse<Advertisement>>(
      `/api/advertisements?${searchParams.toString()}`
    );
    return response;
  }

  async getActiveAdvertisements(position?: string): Promise<Advertisement[]> {
    const searchParams = new URLSearchParams();
    if (position) {
      searchParams.append('position', position);
    }
    searchParams.append('active', 'true');

    const response = await this.request<PaginatedResponse<Advertisement>>(
      `/api/advertisements/active?${searchParams.toString()}`
    );
    return response.data;
  }

  // File Upload API
  async uploadCarImage(carListingId: number, file: File): Promise<{ id: number; imageUrl: string; fileName: string; fileSize: number }> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await this.request<ApiResponse<{ id: number; imageUrl: string; fileName: string; fileSize: number }>>(
      `/api/listings/${carListingId}/images`,
      {
        method: 'POST',
        body: formData,
      }
    );
    return response.data;
  }

  async uploadApplicationDocument(applicationId: number, file: File, documentType: string): Promise<{ id: number; documentUrl: string; fileName: string; fileSize: number; documentType: string }> {
    console.log('API Service: uploadApplicationDocument called with:', { applicationId, fileName: file.name, fileSize: file.size, documentType });
    
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    console.log('API Service: FormData created, sending request to:', `/api/applications/${applicationId}/documents`);

    const response = await this.request<ApiResponse<{ id: number; documentUrl: string; fileName: string; fileSize: number; documentType: string }>>(
      `/api/applications/${applicationId}/documents`,
      {
        method: 'POST',
        body: formData,
      }
    );
    
    console.log('API Service: Upload response received:', response);
    return response.data;
  }

  async uploadDealerDocument(dealerId: number, file: File, documentType: string): Promise<{ id: number; documentUrl: string; fileName: string; fileSize: number; documentType: string }> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    const response = await this.request<ApiResponse<{ id: number; documentUrl: string; fileName: string; fileSize: number; documentType: string }>>(
      `/api/dealers/${dealerId}/documents`,
      {
        method: 'POST',
        body: formData,
      }
    );
    return response.data;
  }

  // Image Management API
  async getCarImages(carListingId: number): Promise<{ id: number; imageUrl: string; fileName: string; fileSize: number; isPrimary?: boolean }[]> {
    console.log('API Service: Calling getCarImages with carListingId:', carListingId);
    console.log('API Service: API URL:', `/api/listings/${carListingId}/images`);
    
    try {
      const response = await this.request<any>(
        `/api/listings/${carListingId}/images`
      );
      
      console.log('API Service: Raw response:', response);
      console.log('API Service: Response data:', response.data);
      
      // Backend returns { success, data: { carListingId, images, total } } so use response.data.images
      const imagesArray =
        response.data?.images ??
        response.data?.data?.images;
      if (!Array.isArray(imagesArray)) {
        console.log('API Service: No images array in response, returning empty array');
        return [];
      }
      
      // Check if images array exists and is not undefined
      if (!imagesArray || !Array.isArray(imagesArray)) {
        console.log('API Service: Images array is not valid, returning empty array');
        return [];
      }
      
      // Transform the backend response to match frontend interface
      const transformedImages = imagesArray.map(img => ({
        id: img.id,
        imageUrl: img.image_url,
        fileName: img.file_name,
        fileSize: img.file_size,
        isPrimary: img.is_primary === 1 || img.is_primary === true
      }));
      
      console.log('API Service: Transformed images:', transformedImages);
      return transformedImages;
    } catch (error) {
      console.error('API Service: Error in getCarImages:', error);
      throw error;
    }
  }

  async setPrimaryImage(carListingId: number, imageId: number): Promise<void> {
    await this.request(`/api/listings/${carListingId}/images/${imageId}/primary`, {
      method: 'PUT',
    });
  }

  async updateCarImages(carListingId: number, images: string[]): Promise<void> {
    await this.request(`/api/listings/${carListingId}/images`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ images }),
    });
  }

  async deleteFile(fileId: number, tableName: 'car_images' | 'application_documents' | 'dealer_documents'): Promise<void> {
    await this.request(`/api/files/${fileId}?table=${tableName}`, {
      method: 'DELETE',
    });
  }

  // Pricing Configuration API
  async getPricingConfig(): Promise<{ apr: number; markupCap: number; sourceScore: number; safetyMargin: number }> {
    const response = await this.request<ApiResponse<{ apr: number; markupCap: number; sourceScore: number; safetyMargin: number }>>('/api/pricing-config');
    return response.data;
  }

  async updatePricingConfig(config: { apr?: number; markupCap?: number; sourceScore?: number; safetyMargin?: number }): Promise<{ apr: number; markupCap: number; sourceScore: number; safetyMargin: number }> {
    const response = await this.request<ApiResponse<{ apr: number; markupCap: number; sourceScore: number; safetyMargin: number }>>('/api/pricing-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
    return response.data;
  }

  // Buyer Leads API
  async submitBuyerLead(data: {
    full_name: string;
    phone_number: string;
    email_address: string;
    city: string;
    state_province: string;
    credit_score: number;
    employment_status?: string;
    revenue_range?: string;
  }): Promise<any> {
    const response = await this.request<ApiResponse<any>>('/api/buyer-leads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }
}

export const apiService = new ApiService(API_BASE_URL); 